import { cloneElement, CSSProperties, KeyboardEvent, MouseEvent, PropsWithChildren, ReactElement } from 'react';

import { EvtKeys } from 'app/types';

// Local Typings
interface Props<TElement extends HTMLElement> {
  ariaChecked?: boolean;
  ariaExpanded?: boolean;
  ariaHasPopUp?: boolean;
  ariaLabel: string;
  ariaPressed?: boolean;
  ariaSelected?: boolean;
  children: ReactElement<{ props: unknown; style?: CSSProperties }>;
  disabled?: boolean;
  onClick: (evt: KeyboardEvent<TElement> | MouseEvent<TElement>) => void;
  role: NodeElementRole;
}

type NodeElementRole = 'button' | 'checkbox' | 'columnheader' | 'link' | 'radio';

// Component Definition
const AriaClickHandler = <TElement extends HTMLElement>({
  ariaChecked,
  ariaExpanded,
  ariaHasPopUp,
  ariaLabel,
  ariaPressed,
  ariaSelected,
  children,
  disabled,
  onClick,
  role,
  ...props
}: PropsWithChildren<Props<TElement>>) => {
  function handleKeyDown(evt: KeyboardEvent<TElement>) {
    // allow nested items to have keydown events
    if (evt.target !== evt.currentTarget) {
      return;
    }

    if (evt.key as EvtKeys === EvtKeys.Enter || evt.key as EvtKeys === EvtKeys.Space) {
      onClick(evt);
    }
  }

  const ariaClickProps = disabled
    ? {
      ...props,
    }
    : {
      ['aria-checked']: ariaChecked,
      ['aria-expanded']: ariaExpanded,
      ['aria-haspopup']: ariaHasPopUp,
      ['aria-label']: ariaLabel,
      ['aria-pressed']: ariaPressed,
      ['aria-selected']: ariaSelected,
      onClick,
      onKeyDown: handleKeyDown,
      role,
      tabIndex: 0,
      ...props,
    };

  return cloneElement(children,
    {
      ...ariaClickProps,
      ...children.props,
      // @emotion doesn't play well with components that were not created with the `css` prop
      // so using `style` here to get around that limitation
      // see: https://emotion.sh/docs/css-prop#gotchas
      style: disabled
        ? undefined
        : {
          cursor: 'pointer',
        },
    });
};

export default AriaClickHandler;
